import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { isEmpty, pick } from 'lodash';
import styled from 'styled-components';

import { FormStepProgress } from '@shared/FormStepProgress';
import media from '../../../utils/mediaQueries';
import stores from '../../../stores';
import { FunnelStep } from '../../../stores/DemarcheFunnelStore';
import { spacing } from '../tokens';
import {
  AddressInfo, CivilState, Consents, ContactInfo, DemarcheChoice, DemarcheFlowConfig, ExtraFieldValues,
} from '../types';
import { AntsFormStep } from './AntsFormStep';
import { ConfirmationStep } from './ConfirmationStep';
import { ContactStep } from './ContactStep';
import { FreePathStep } from './FreePathStep';
import { PaymentStep } from './PaymentStep';
import { SituationStep } from './SituationStep';
import { SummaryChoiceStep } from './SummaryChoiceStep';
import { isMinorBirthDate } from './submitMapping';

const Container = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: ${spacing.xl} 0;

  ${media.mobile`
    padding: 20px 0;
  `}
`;

const HUB_PATH = '/demarches-permis';

const GENERIC_ERROR_MESSAGE = 'Une erreur est survenue, veuillez réessayer.';
const INCOMPLETE_ORDER_MESSAGE = 'Certaines informations ou pièces obligatoires manquent à votre dossier. '
  + 'Complétez le formulaire puis réessayez.';

const errorMessageFor = (code: string | null): string => (
  code === 'incomplete_order' ? INCOMPLETE_ORDER_MESSAGE : GENERIC_ERROR_MESSAGE
);

type StepOrder = {
  order: FunnelStep[];
  labels: string[];
};

/**
 * Mirrors the prototype's `currentOrder()`/`updateProgress()`: the funnel branches (no
 * accompagnement at all / free path / paid path) rather than following one fixed sequence, so the
 * progress bar's steps depend on the flow and the free-vs-paid choice, not just the current step.
 */
const getStepOrder = (hasAccompagnement: boolean, choice: DemarcheChoice): StepOrder => {
  if (!hasAccompagnement) {
    return { order: ['situation', 'summary', 'free'], labels: ['Situation', 'Récapitulatif', 'Résultat'] };
  }
  if (choice === 'payant') {
    return {
      order: ['situation', 'contact', 'summary', 'ants-form', 'payment'],
      labels: ['Situation', 'Coordonnées', 'Récapitulatif', 'Vos informations', 'Paiement'],
    };
  }
  return {
    order: ['situation', 'contact', 'summary', 'free'],
    labels: ['Situation', 'Coordonnées', 'Récapitulatif', 'Résultat'],
  };
};

type DemarcheFunnelContainerProps = {
  config: DemarcheFlowConfig;
  // Answer carried over from the service page's selector — see DemarcheFunnelStore#init.
  initialSituation?: string | null;
  // Carried on the query string when Stripe returns the user to the tunnel (backend-plan.md §7.2).
  resumeToken?: string | null;
  paymentResult?: string | null;
};

const DemarcheFunnelContainer: React.FC<DemarcheFunnelContainerProps> = observer(({
  config,
  initialSituation = null,
  resumeToken = null,
  paymentResult = null,
}) => {
  const { demarcheFunnelStore, userStore } = stores;
  const {
    situation, contactInfo, choice, civilState, address, extraFieldValues, consents, step, order,
    isOrderLoading, orderErrorStatus, orderErrorCode, orderErrorMessages,
    isPaymentLoading, paymentErrorStatus, paymentErrorCode, handledBy, caseId,
    documents, documentsComplete, isDocumentsLoading, uploadingDocumentKeys, documentUploadErrors,
  } = demarcheFunnelStore;

  // §F2a: the account section needs to know, on mount, whether this visitor already has a
  // session — GET /api/web/v1/student_sessions, which answers "nobody" with a 200 so an anonymous
  // visitor opening the tunnel does not collect a 401.
  const { userInfo } = userStore;
  const isSignedIn = !isEmpty(userInfo);

  const isExistingEmailError = orderErrorCode === 'existing_email';
  const passwordErrorMessage = orderErrorMessages?.password || null;
  const orderErrorMessage = orderErrorStatus && !isExistingEmailError && !passwordErrorMessage
    ? errorMessageFor(orderErrorCode)
    : null;
  const paymentErrorMessage = paymentErrorStatus ? errorMessageFor(paymentErrorCode) : null;

  const hasAccompagnement = Boolean(config.accompagnement);

  const redirectUrlFor = (value: string | null): string | null => {
    const redirect = (config.situationRedirects || []).find((item) => item.situation === value);

    return redirect ? `${redirect.url}#situation` : null;
  };

  useEffect(() => {
    const isKnownSituation = config.situationStep.options.some(
      (option) => option.value === initialSituation,
    );
    const redirectUrl = redirectUrlFor(initialSituation);
    const prefilled = isKnownSituation && !redirectUrl ? initialSituation : null;

    demarcheFunnelStore.init(config.slug, prefilled);

    if (redirectUrl) {
      window.location.replace(redirectUrl);
      return;
    }

    if (prefilled) {
      demarcheFunnelStore.goToStep(hasAccompagnement ? 'contact' : 'summary');
    }
  }, [config.slug, initialSituation]);

  // Coming back from Stripe (or, later, a genuine resume link): pick the order back up and land on
  // whichever screen its real status calls for — the return itself proves nothing.
  useEffect(() => {
    if (!resumeToken) {
      return;
    }

    demarcheFunnelStore.resumeOrder(config.slug, resumeToken).then((success: boolean) => {
      if (!success) {
        return;
      }
      if (paymentResult === 'success') {
        demarcheFunnelStore.setChoice('payant');
        demarcheFunnelStore.goToStep('success');
        demarcheFunnelStore.fetchHandledBy();
      } else {
        demarcheFunnelStore.setChoice('payant');
        demarcheFunnelStore.goToStep('payment');
      }
    });
  }, [resumeToken]);

  useEffect(() => {
    userStore.loadSession();
  }, []);

  // Hydrates section 4's upload rows once the order exists and the ANTS form is reached (initial
  // entry or a reload/resume landing back here) — the checklist itself lives on `order` already.
  useEffect(() => {
    if (step === 'ants-form' && order) {
      demarcheFunnelStore.fetchDocuments();
    }
  }, [step, order?.accessToken]);

  const handleSignIn = async (email: string, password: string): Promise<string | null> => {
    let errorMessage: string | null = null;
    const fakeFormikBag = {
      setErrors: (errors: Record<string, string>) => {
        errorMessage = errors.email || 'Identifiants incorrects.';
      },
    };

    try {
      await userStore.signIn({ email, password }, fakeFormikBag);
    } catch (e) {
      errorMessage = errorMessage || 'Identifiants incorrects.';
    }

    return errorMessage;
  };

  // A flow can ask one situation fewer questions than the others (Flows::Flow#extra_fields_for).
  // The answers are filtered with the fields: a value typed before the situation was changed must
  // not travel to the order, where the document rules would read it.
  const visibleExtraFields = useMemo(
    () => config.extraFields.filter(
      (field) => !(field.hiddenForSituations || []).includes(situation || ''),
    ),
    [config.extraFields, situation],
  );
  const visibleExtraFieldValues = useMemo(
    () => pick(extraFieldValues, visibleExtraFields.map((field) => field.id)),
    [extraFieldValues, visibleExtraFields],
  );

  const isExcludedSituation = Boolean(
    config.noAccompagnementSituations && situation && config.noAccompagnementSituations.includes(situation),
  );
  const situationAllowsAccompagnement = hasAccompagnement && !isExcludedSituation;

  const handleSituationSubmit = (): void => {
    if (!situation) {
      return;
    }

    const redirectUrl = redirectUrlFor(situation);

    if (redirectUrl) {
      window.location.assign(redirectUrl);
      return;
    }

    demarcheFunnelStore.goToStep(hasAccompagnement ? 'contact' : 'summary');
  };

  const handleContactSubmit = (values: ContactInfo): void => {
    demarcheFunnelStore.setContactInfo(values);
    demarcheFunnelStore.submitContact(situation, values);
    demarcheFunnelStore.goToStep('summary');
  };

  const handleChoose = (selectedChoice: DemarcheChoice): void => {
    if (selectedChoice !== 'payant') {
      demarcheFunnelStore.setChoice(selectedChoice);
      demarcheFunnelStore.goToStep('free');
      return;
    }

    demarcheFunnelStore.createOrder(situation, contactInfo).then((success: boolean) => {
      if (success) {
        demarcheFunnelStore.setChoice(selectedChoice);
        demarcheFunnelStore.goToStep('ants-form');
      }
    });
  };

  const handleAntsSubmit = (
    nextCivilState: CivilState,
    nextAddress: AddressInfo,
    nextExtraFieldValues: ExtraFieldValues,
    nextConsents: Consents,
    password?: string,
  ): void => {
    demarcheFunnelStore.setCivilState(nextCivilState);
    demarcheFunnelStore.setAddress(nextAddress);
    demarcheFunnelStore.setExtraFieldValues(nextExtraFieldValues);
    demarcheFunnelStore.setConsents(nextConsents);
    demarcheFunnelStore.updateOrder(nextCivilState, nextAddress, nextExtraFieldValues).then((success: boolean) => {
      if (!success) {
        return;
      }
      const isMinor = isMinorBirthDate(nextCivilState.dateNaissance);

      demarcheFunnelStore.submitOrder(nextConsents, password, isMinor).then((submitted: boolean) => {
        if (submitted) {
          demarcheFunnelStore.goToStep('payment');
        }
      });
    });
  };

  const handleAntsFieldsChange = useCallback((
    nextCivilState: CivilState, nextAddress: AddressInfo, nextExtraFieldValues: ExtraFieldValues,
  ): void => {
    demarcheFunnelStore.scheduleChecklistRefresh(nextCivilState, nextAddress, nextExtraFieldValues);
  }, [demarcheFunnelStore]);

  const handleUploadDocument = (documentKey: string, file: File): Promise<boolean> => (
    demarcheFunnelStore.uploadDocument(documentKey, file)
  );

  const handleDeleteDocument = (id: string): Promise<boolean> => demarcheFunnelStore.deleteDocument(id);

  const handlePay = (): void => {
    demarcheFunnelStore.createPayment();
  };

  const handleRefreshConfirmation = (): void => {
    if (!order) {
      return;
    }
    demarcheFunnelStore.resumeOrder(config.slug, order.accessToken).then((success: boolean) => {
      if (success) {
        demarcheFunnelStore.fetchHandledBy();
      }
    });
  };

  const selectedOption = config.situationStep.options.find(
    (option) => option.value === situation,
  );

  const renderStepContent = (): JSX.Element => {
    switch (step) {
      case 'contact':
        return (
          <ContactStep
            initialValues={contactInfo}
            onSubmit={handleContactSubmit}
            onBack={() => demarcheFunnelStore.goToStep('situation')}
          />
        );
      case 'summary':
        return (
          <SummaryChoiceStep
            demarcheName={config.name}
            price={config.price}
            situationLabel={selectedOption ? selectedOption.label : ''}
            contactInfo={contactInfo}
            accompagnement={config.accompagnement}
            situationAllowsAccompagnement={situationAllowsAccompagnement}
            noAccompagnementMessage={config.noAccompagnementMessage}
            isChoosingPayant={isOrderLoading}
            payantErrorMessage={orderErrorMessage}
            onChoose={handleChoose}
            onBack={() => demarcheFunnelStore.goToStep(hasAccompagnement ? 'contact' : 'situation')}
          />
        );
      case 'ants-form':
        return (
          <AntsFormStep
            extraFields={visibleExtraFields}
            initialCivilState={civilState}
            initialAddress={address}
            initialExtraFieldValues={visibleExtraFieldValues}
            initialConsents={consents}
            contactInfo={contactInfo}
            isSubmitting={isOrderLoading}
            submitErrorMessage={orderErrorMessage}
            submitErrorCode={orderErrorCode}
            passwordErrorMessage={passwordErrorMessage}
            isSignedIn={isSignedIn}
            signedInEmail={userInfo?.email || null}
            documentChecklist={order?.documentChecklist || []}
            documentNotices={order?.notices || []}
            uploadedDocuments={documents}
            documentsComplete={documentsComplete}
            isDocumentsLoading={isDocumentsLoading}
            uploadingDocumentKeys={uploadingDocumentKeys}
            documentUploadErrors={documentUploadErrors}
            onFieldsChange={handleAntsFieldsChange}
            onUploadDocument={handleUploadDocument}
            onDeleteDocument={handleDeleteDocument}
            onSignIn={handleSignIn}
            onSubmit={handleAntsSubmit}
            onBack={() => demarcheFunnelStore.goToStep('summary')}
            onEditContact={() => demarcheFunnelStore.goToStep('contact')}
          />
        );
      case 'payment':
        if (!order) {
          return (
            <SituationStep
              situationStep={config.situationStep}
              selectedSituation={situation}
              onSelectSituation={demarcheFunnelStore.setSituation}
              onSubmit={handleSituationSubmit}
              backHref={`/demarches-permis/${config.slug}`}
            />
          );
        }
        return (
          <PaymentStep
            order={order}
            demarcheName={config.name}
            isPaying={isPaymentLoading}
            paymentErrorMessage={paymentErrorMessage}
            wasCancelled={paymentResult === 'cancelled'}
            onPay={handlePay}
            onBack={resumeToken ? undefined : () => demarcheFunnelStore.goToStep('ants-form')}
          />
        );
      case 'success':
        if (!order) {
          return (
            <SituationStep
              situationStep={config.situationStep}
              selectedSituation={situation}
              onSelectSituation={demarcheFunnelStore.setSituation}
              onSubmit={handleSituationSubmit}
              backHref={`/demarches-permis/${config.slug}`}
            />
          );
        }
        return (
          <ConfirmationStep
            order={order}
            demarcheName={config.name}
            serviceHref={`/demarches-permis/${config.slug}`}
            hubHref={HUB_PATH}
            handledBy={handledBy}
            caseId={caseId}
            isRefreshing={isOrderLoading}
            onRefresh={handleRefreshConfirmation}
          />
        );
      case 'free':
        return (
          <FreePathStep
            demarcheName={config.name}
            officialUrl={config.officialUrl}
            guideUrl={config.guideUrl}
            hubHref={HUB_PATH}
            accompagnement={config.accompagnement}
            situationAllowsAccompagnement={situationAllowsAccompagnement}
            isSwitchingToPaid={isOrderLoading}
            switchToPaidErrorMessage={orderErrorMessage}
            onBack={() => demarcheFunnelStore.goToStep('summary')}
            onSwitchToPaid={() => handleChoose('payant')}
          />
        );
      default:
        return (
          <SituationStep
            situationStep={config.situationStep}
            selectedSituation={situation}
            onSelectSituation={demarcheFunnelStore.setSituation}
            onSubmit={handleSituationSubmit}
            backHref={`/demarches-permis/${config.slug}`}
          />
        );
    }
  };

  const { order: stepOrder, labels } = getStepOrder(hasAccompagnement, choice);
  const currentStepIndex = Math.max(stepOrder.indexOf(step), 0);
  // The confirmation screen is a destination, not a step to track progress against.
  const showProgress = step !== 'success';

  return (
    <Container>
      {showProgress && <FormStepProgress steps={labels} currentStep={currentStepIndex} />}
      {renderStepContent()}
    </Container>
  );
});

export { DemarcheFunnelContainer, getStepOrder };
